import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Alert, Spinner } from "react-bootstrap";

function AddToCollectionModal({ show, onHide, recipeId, recipeName }) {
  const [collections, setCollections] = useState([]);
  const [selectedCollections, setSelectedCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showNewCollection, setShowNewCollection] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState("");
  const [newCollectionDescription, setNewCollectionDescription] = useState("");

  // Fetch user's collections and which ones contain this recipe
  useEffect(() => {
    if (show && recipeId) {
      fetchCollectionsData();
    }
  }, [show, recipeId]);

  const fetchCollectionsData = async () => {
    setLoading(true);
    setError("");
    
    try {
      // Fetch all user collections
      const collectionsResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/api/collections`,
        { credentials: "include" }
      );
      
      if (!collectionsResponse.ok) {
        throw new Error("Failed to fetch collections");
      }
      
      const collectionsData = await collectionsResponse.json();
      
      // Fetch collections containing this recipe
      const recipeCollectionsResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/api/recipes/${recipeId}/collections`,
        { credentials: "include" }
      );
      
      if (!recipeCollectionsResponse.ok) {
        throw new Error("Failed to fetch recipe collections");
      }
      
      const recipeCollectionsData = await recipeCollectionsResponse.json();
      const recipeCollectionIds = recipeCollectionsData.collections.map(c => c.id);
      
      setCollections(collectionsData.collections || []);
      setSelectedCollections(recipeCollectionIds);
    } catch (err) {
      console.error("Error fetching collections:", err);
      setError("Failed to load collections");
    } finally {
      setLoading(false);
    }
  };

  const handleCollectionToggle = (collectionId) => {
    setSelectedCollections(prev => {
      if (prev.includes(collectionId)) {
        return prev.filter(id => id !== collectionId);
      } else {
        return [...prev, collectionId];
      }
    });
  };

  const handleCreateNewCollection = async () => {
    if (!newCollectionName.trim()) {
      setError("Collection name is required");
      return;
    }

    try {
      setSaving(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/collections`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: newCollectionName.trim(),
            description: newCollectionDescription.trim(),
            isPublic: false
          })
        }
      );

      if (!response.ok) {
        throw new Error("Failed to create collection");
      }

      const data = await response.json();
      
      // Refresh collections and add the new one to selected
      await fetchCollectionsData();
      setSelectedCollections(prev => [...prev, data.collectionId]);
      
      // Reset form
      setNewCollectionName("");
      setNewCollectionDescription("");
      setShowNewCollection(false);
    } catch (err) {
      console.error("Error creating collection:", err);
      setError("Failed to create collection");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");

    try {
      // Get current collections for this recipe
      const currentResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/api/recipes/${recipeId}/collections`,
        { credentials: "include" }
      );
      
      const currentData = await currentResponse.json();
      const currentCollectionIds = currentData.collections.map(c => c.id);

      // Determine which collections to add and remove
      const toAdd = selectedCollections.filter(id => !currentCollectionIds.includes(id));
      const toRemove = currentCollectionIds.filter(id => !selectedCollections.includes(id));

      // Add recipe to new collections
      for (const collectionId of toAdd) {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/collections/${collectionId}/recipes`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ recipeId })
          }
        );

        if (!response.ok) {
          throw new Error("Failed to add recipe to collection");
        }
      }

      // Remove recipe from unchecked collections
      for (const collectionId of toRemove) {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/collections/${collectionId}/recipes/${recipeId}`,
          {
            method: "DELETE",
            credentials: "include"
          }
        );

        if (!response.ok) {
          throw new Error("Failed to remove recipe from collection");
        }
      }

      onHide();
    } catch (err) {
      console.error("Error updating collections:", err);
      setError("Failed to update collections");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="md">
      <Modal.Header closeButton>
        <Modal.Title>Add to Collections</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {recipeName && (
          <p className="text-muted mb-3">
            Adding "<strong>{recipeName}</strong>" to collections
          </p>
        )}

        {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}

        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
            <p className="mt-2">Loading collections...</p>
          </div>
        ) : (
          <>
            {collections.length === 0 && !showNewCollection ? (
              <div className="text-center py-4">
                <p>You don't have any collections yet.</p>
                <Button 
                  variant="primary" 
                  onClick={() => setShowNewCollection(true)}
                >
                  Create Your First Collection
                </Button>
              </div>
            ) : (
              <>
                <div className="collection-list" style={{ maxHeight: "300px", overflowY: "auto" }}>
                  {collections.map(collection => (
                    <Form.Check
                      key={collection.id}
                      type="checkbox"
                      id={`collection-${collection.id}`}
                      label={
                        <div>
                          <strong>{collection.name}</strong>
                          {collection.description && (
                            <small className="text-muted d-block">{collection.description}</small>
                          )}
                          <small className="text-muted">
                            {collection.recipe_count || collection.actual_recipe_count || 0} recipes
                          </small>
                        </div>
                      }
                      checked={selectedCollections.includes(collection.id)}
                      onChange={() => handleCollectionToggle(collection.id)}
                      className="mb-3"
                    />
                  ))}
                </div>

                {!showNewCollection && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="mt-3"
                    onClick={() => setShowNewCollection(true)}
                  >
                    + Create New Collection
                  </Button>
                )}
              </>
            )}

            {showNewCollection && (
              <div className="border rounded p-3 mt-3">
                <h6>Create New Collection</h6>
                <Form.Group className="mb-2">
                  <Form.Control
                    type="text"
                    placeholder="Collection name"
                    value={newCollectionName}
                    onChange={(e) => setNewCollectionName(e.target.value)}
                    disabled={saving}
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Description (optional)"
                    value={newCollectionDescription}
                    onChange={(e) => setNewCollectionDescription(e.target.value)}
                    disabled={saving}
                  />
                </Form.Group>
                <div className="d-flex gap-2">
                  <Button
                    variant="success"
                    size="sm"
                    onClick={handleCreateNewCollection}
                    disabled={saving || !newCollectionName.trim()}
                  >
                    Create
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setShowNewCollection(false);
                      setNewCollectionName("");
                      setNewCollectionDescription("");
                    }}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSave} 
          disabled={saving || loading}
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default AddToCollectionModal;